import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, BehaviorSubject, throwError, of, Subscription } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { ResultData } from '@app/core/_common/model';
import { environment } from '@app/env-service';

import { RFQ, RFQAttachment } from '../store/quotes.model';

@Injectable({
  providedIn: 'root',
})
export class RfqService {
  url :string = environment.url;
  urlV2 :string = environment.urlV2;

  private readonly _record = new BehaviorSubject<ResultData>(<ResultData>{});
  public record$ = this._record.asObservable();

  private readonly _activeRecord = new BehaviorSubject<RFQ>(<RFQ>{});
  public activeRecord$ = this._activeRecord.asObservable();

  private readonly _record_channel_shipping = new BehaviorSubject<any>(<any>{});
  public record_channel_shipping$ = this._record_channel_shipping.asObservable();

  public gettingRecords :boolean = false;

  private readonly _rfqs = new BehaviorSubject<RFQ[]>(<RFQ[]>[]);
  rfqs$ = this._rfqs.asObservable();

  private readonly _consolidatedSku = new BehaviorSubject<RFQ>(<RFQ>{});
  consolidatedSku$ = this._consolidatedSku.asObservable();

  private readonly _gettingSkus = new BehaviorSubject<boolean>(<boolean>false);
  gettingSkus$ = this._gettingSkus.asObservable();

  private params :any = {
    per_page: 20,
    page: 1,
  };

  private readonly _generatedSku = new BehaviorSubject<any>(<any>null);
  private readonly _generatingSku = new BehaviorSubject<boolean>(<boolean>false);

  generatedSku$ = this._generatedSku.asObservable();
  generatingSku$ = this._generatingSku.asObservable();

  quoteTypes :any = [
    { id: 1, text: this.translate.instant('RFQ.QUICK_QUOTE') },
    { id: 2, text: this.translate.instant('RFQ.PRODUCT_RECOMMENDATION') },
    { id: 16, text: this.translate.instant('RFQ.EXACT_QUOTE') },
  ];
  materialCosts :any = {
    computed_weighted: 'Weighted',
    computed_fifo: 'FIFO',
    computed_lastpo: 'Latest PO',
  };

  weightUnits :any[] = ['lbs', 'oz'];

  private readonly _loadingRfq = new BehaviorSubject<boolean>(false);
  loadingRfq$ = this._loadingRfq.asObservable();

  private readonly _gettingRecords = new BehaviorSubject<boolean>(false);
  gettingRecords$ = this._gettingRecords.asObservable();

  constructor(
    private http :HttpClient,
    private translate :TranslateService,
  ) {
    // this.refreshList();
  }

  getVariantSkusByParentIds(parentIds :any, params :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/variant_skus`, {
      parentIds: parentIds,
      params: params,
    });
  }

  getPdf(id :number) {
    Swal.fire({
      backdrop: true,
      allowOutsideClick: false,
      text: 'Exporting to PDF...',
    });
    Swal.showLoading();

    return this.http
      .get(`${this.url}/rfqs/${id}/pdf`, { responseType: 'blob' })
      .subscribe(
        (resp :any) => {
          Swal.hideLoading();
          const downloadURL = URL.createObjectURL(resp);
          window.open(downloadURL);
          Swal.fire('Success!', 'Exported to PDF.', 'success');
        },
        (err) => {
          Swal.hideLoading();
          Swal.fire(
            'Error!',
            'Error generating PDF. Please try again later or contact support.',
            'error',
          );
        },
      );
  }

  delete(id :number, discontinue ?:boolean) :Observable<any> {
    const params :any = {};
    if (discontinue) {
      params.params = { discontinue: 1 };
    }

    return this.http.delete<RFQ>(`${this.url}/rfqs/${id}`, params).pipe(
      tap((resp) => {
        const record = this._record.getValue();
        const variantIds = [];
        if (record && record.data && record.data.length > 0) {
          const idx = record.data.findIndex((row) => {
            return row.id == id;
          });

          if (idx === -1) variantIds.push(id);

          record.data.splice(idx, 1);
          record.total -= 1;
          this._record.next(record);
        }

        if (variantIds.length > 0) {
          record.data = record.data.map((row) => {
            if (!row.hasOwnProperty('variant_skus')) return row;

            return {
              ...row,
              variant_skus: row.variant_skus.filter(
                (e) => !variantIds.includes(e.id),
              ),
            };
          });
          this._record.next(record);
        }
      }),
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  bulkDelete(ids :any, discontinue ?:boolean) :Observable<any> {
    const params :any = {};
    if (discontinue) {
      params.params = {
        discontinue: 1,
      };
    }

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {
        ids: ids,
      },
    };

    return this.http.delete<RFQ>(`${this.url}/rfqs_bulk_delete`, options).pipe(
      tap((resp) => {
        const record :any = this._record.getValue();
        if (record && record.data && record.data.length > 0) {
          const variantIds = [];
          ids.forEach(function (id) {
            const idx = record.data.findIndex((row) => {
              return row.id == id;
            });

            if (idx === -1) variantIds.push(id);

            record.data.splice(idx, 1);
            record.total -= 1;
            this._record.next(record);
          }, this);

          // delete if variant sku
          if (variantIds.length > 0) {
            record.data = record.data.map((row) => {
              if (!row.hasOwnProperty('variant_skus')) return row;

              return {
                ...row,
                variant_skus: row.variant_skus.filter(
                  (e) => !variantIds.includes(e.id),
                ),
              };
            });
            this._record.next(record);
          }
        }
      }),
      catchError((error) => {
        return throwError(error);
      }),
    );

    return;
  }

  deleteRfqLocation(rfqId :string, locationId) :Observable<any> {
    return this.http.delete(`${this.url}/rfqs/${rfqId}/locations/${locationId}`);
  }

  setActive(rfq :RFQ, getFiles ?:boolean, getQuotations ?:boolean) {
    try {
      rfq.product_variants = JSON.parse(rfq.product_variants);
    } catch (e) {}
    try {
      this.parseWeightArrayJson(rfq);
    } catch (e) {}
    this._activeRecord.next(rfq);
    if (getFiles) {
      this.getActiveRfqAttachments();
    }
  }

  setActiveById(id :number, getFiles ?:boolean, getQuotations ?:boolean) {
    const record = this._record.getValue();
    if (record && record.data && record.data.length > 0) {
      const rfq = record.data.find((row) => {
        return row.id == id;
      });
      if (rfq) {
        this.setActive(rfq, getFiles, getQuotations);

        return;
      }
    }
    this.getById(id).subscribe((rfq :RFQ) => {
      this.setActive(this.formatData(rfq), getFiles, getQuotations);
    });
  }

  getActiveRfq() {
    return this._activeRecord.getValue();
  }

  getActiveRfqAttachments() {
    const rfq :RFQ = this._activeRecord.getValue();

    return this.http
      .get(`${this.url}/rfqs/${rfq.id}/attachments`)
      .subscribe((files :RFQAttachment[]) => {
        rfq.rfq_attachments = files;
        this._activeRecord.next(rfq);
      });
  }

  getById(id :number, fresh ?:boolean) :Observable<RFQ> {
    if (!fresh) {
      const activeRfq = this.getActiveRfq();
      if (activeRfq && activeRfq.id == id) {
        return of(activeRfq);
      }
    }

    return this.http.get<RFQ>(`${this.url}/rfqs/${id}`).pipe(
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  refreshList() {
    this.getV2Api(this.params);
  }

  searchSKU(params :any) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/search_sku`, { params: params });
  }

  searchSKUDropdown(params :any) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/search_sku_dropdown`, {
      params: params,
    });
  }

  search(params :any) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/search`, { params: params });
  }

  getAsync(params :any) :Observable<any> {
    return this.http.get(`${this.url}/rfqs`, { params: params });
  }

  export(params :any) :Observable<any> {
    return this.http.get(`${this.url}/exports/master_skus`, { params: params });
  }

  exportSelected(fd :FormData) :Observable<any> {
    return this.http.post(`${this.url}/exports/selected_skus`, fd);
  }

  get(params :any) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/rfqs`, { params: params })
      .subscribe((resp :ResultData) => {
        resp.data.forEach((row) => {
          this.formatData(row);
        });
        this._record.next(resp);
        this.gettingRecords = false;
      });
  }

  getV2Api(params :any) {
    this.gettingRecords = true;
    this._loadingRfq.next(true);

    return this.http
      .get(`${this.url}/v2/skus`, { params: params })
      .subscribe((resp :ResultData) => {
        resp.data.forEach((row) => {
          this.formatData(row);
        });
        this._record.next(resp);
        this._rfqs.next(resp.data);
        this._loadingRfq.next(false);
        this.gettingRecords = false;
      });
  }

  getV2Skus(params :any) {
    return this.http.get(`${this.url}/v2/skus`, { params: params });
  }

  formatData(row :any) {
    // if (row.stock) {
    // 	row.qty_incoming = row.stock.qty_incoming < 0 ? 0 : row.stock.qty_incoming;
    // 	row.qty_pending = row.stock.qty_pending < 0 ? 0 : row.stock.qty_pending;
    // 	row.qty_available = row.stock.qty_onhand - row.qty_pending;
    // 	row.qty_onhand = row.qty_onhand;
    // }
    // row.unit_costs = this.getUnitCosts(row.qty_available, row.latest_purchase_orders, (row.base_price ? row.base_price.price : 0));
    row.unit_costs = this.getSkuUnitCosts(row);

    if (row.variant_skus && row.variant_skus.length > 0) {
      row.variant_skus.forEach((vsku :any) => {
        // if (vsku.stock) {
        // 	vsku.qty_incoming = vsku.stock.qty_incoming < 0 ? 0 : vsku.stock.qty_incoming;
        // 	vsku.qty_pending = vsku.stock.qty_pending < 0 ? 0 : vsku.stock.qty_pending;
        // 	vsku.qty_available = vsku.stock.qty_onhand - vsku.stock.qty_pending;
        // }
        // vsku.unit_costs = this.getUnitCosts(vsku.qty_available, vsku.latest_purchase_orders, vsku.base_price ? vsku.base_price.price : 0);
        vsku.unit_costs = this.getSkuUnitCosts(vsku);
      });
    }

    try {
      row.product_variants = JSON.parse(row.product_variants);
    } catch (e) {}
    try {
      this.parseWeightArrayJson(row);
    } catch (e) {}

    try {
      row.product_weight = JSON.parse(row.product_weight);
    } catch (e) {}
    try {
      row.box_weight = JSON.parse(row.box_weight);
    } catch (e) {}
    try {
      row.carton_weight = JSON.parse(row.carton_weight);
    } catch (e) {}
    try {
      row.pallet_weight = JSON.parse(row.pallet_weight);
    } catch (e) {}

    try {
      row.product_link = JSON.parse(row.product_link);
    } catch (e) {
      if (row.product_link && row.product_link.trim().length > 0) {
        row.product_link = [row.product_link];
      }
    }
    try {
      row.photo = JSON.parse(row.photo);
    } catch (e) {
      if (row.photo && row.photo.trim().length > 0) {
        row.photo = [row.photo];
      }
    }
    try {
      row.product_design = JSON.parse(row.product_design);
    } catch (e) {
      if (row.product_design && row.product_design.trim().length > 0) {
        row.product_design = [row.product_design];
      }
    }
    try {
      row.materials = JSON.parse(row.materials);
    } catch (e) {
      if (row.materials && row.materials.trim().length > 0) {
        row.materials = [row.materials];
      }
    }
    try {
      row.custom_fields = JSON.parse(row.custom_fields);
    } catch (e) {}

    return row;
  }

  getRfqs(params :any) :Observable<any> {
    return this.http.get(`${this.url}/rfqs`, { params: params });
  }

  getVariantSKUs(parentId :number) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/${parentId}/variant_skus`, {});
  }

  updatePipelineStatus(rfqId :number, status :string) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${rfqId}/pipeline_status`, {
      pipeline_status: status,
    });
  }

  updateRfqVariants(id :number, formData :FormData) :Observable<any> {
    formData.append('status', '1');

    return this.http.post(`${this.url}/rfqs/${id}/variants`, formData).pipe(
      tap((data :any) => {
        const rfq = this.formatData(data.record);
        this.updateRowData(rfq);
      }),
    );
  }

  updateRowData(rowData :RFQ, respRfqs :RFQ[] = []) :void {
    const record = this._record.getValue();
    let rfqList = respRfqs;
    if (!respRfqs || respRfqs.length <= 0) {
      rfqList = record.data;
    }

    if (record && rfqList && rfqList.length > 0) {
      const idx = rfqList.findIndex((row) => {
        return row.id == rowData.id;
      });
      const existingRfq = rfqList[idx];
      if (existingRfq) {
        const rfq = Object.assign(existingRfq, rowData);
        rfqList.splice(idx, 1, rfq);
        this._record.next(record);

        const activeRfq :any = this._activeRecord.getValue();
        if (rowData.id == activeRfq.id) {
          this._activeRecord.next(rfq);
        }

        return;
      }

      if (rowData.variant_parent_id && rowData.variant_parent_id > 0) {
        const parentIdx = rfqList.findIndex((row) => {
          return row.id == rowData.variant_parent_id;
        });

        if (parentIdx > -1) {
          const varIdx = rfqList[parentIdx].variant_skus.findIndex((row) => {
            return row.id === rowData.id;
          });

          const parentRfq = rfqList[parentIdx];
          const variantRfq = parentRfq.variant_skus[varIdx];

          if (parentRfq) {
            const rfq = Object.assign(variantRfq, rowData);
            rfqList[parentIdx].variant_skus.splice(varIdx, 1, rfq);
            this._record.next(record);

            return;
          }
        }

        const activeRfq :any = this._activeRecord.getValue();
        if (rowData.id === activeRfq.id) {
          this._activeRecord.next(rowData);
        }
      }
    }

    const activeRfq :any = this._activeRecord.getValue();
    if (rowData.id == activeRfq.id) {
      this._activeRecord.next(Object.assign(activeRfq, rowData));
    }
  }

  addAlternateSKU(rfqId :number, payload :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${rfqId}/alternate_skus`, payload).pipe(
      tap((resp) => {
        if (resp && resp.id) {
          const activeRfq :any = this._activeRecord.getValue();
          if (activeRfq && activeRfq.id == rfqId) {
            activeRfq.alternate_skus.splice(0, 0, resp);
            this._activeRecord.next(activeRfq);
          } else {
            const record = this._record.getValue();
            if (record && record.data && record.data.length > 0) {
              const rfq :any = record.data.find((row) => {
                return row.id == rfqId;
              });
              if (rfq && rfq.id) {
                rfq.alternate_skus.splice(0, 0, resp);
                this._record.next(record);
              }
            }
          }
        }
      }),
    );
  }

  deleteAlternateSKU(rfqId :number, skuId) :Observable<any> {
    return this.http
      .delete(`${this.url}/rfqs/${rfqId}/alternate_skus/${skuId}`)
      .pipe(
        tap((resp) => {
          if (resp && skuId) {
            const activeRfq :any = this._activeRecord.getValue();
            if (activeRfq && activeRfq.id == rfqId) {
              const idx = activeRfq.alternate_skus.findIndex(
                (row) => row.id == skuId,
              );
              activeRfq.alternate_skus.splice(idx, 1);
              this._activeRecord.next(activeRfq);
            } else {
              const record = this._record.getValue();
              if (record && record.data && record.data.length > 0) {
                const rfq :any = record.data.find((row) => {
                  return row.id == rfqId;
                });
                if (rfq && rfq.id) {
                  const idx = rfq.alternate_skus.findIndex(
                    (row) => row.id == skuId,
                  );
                  rfq.alternate_skus.splice(idx, 1);
                  this._record.next(record);
                }
              }
            }
          }
        }),
      );
  }

  getNext() {
    this.params.page = this.params.page + 1;
    this.refreshList();
  }

  getPrevious() {
    this.params.page = this.params.page - 1;
    this.refreshList();
  }

  setParams(params :any) {
    this.params = params;
  }

  setBulkRfqAdmins(data) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/set_bulk_rfq_admins`, data).pipe(
      tap((resp) => {
        if (resp.rfqs) {
          resp.rfqs.forEach((rfq) => {
            this.updateRowData(rfq);
          });
        }
      }),
    );
  }

  setBulkRfqMembers(data) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/set_bulk_rfq_members`, data).pipe(
      tap((resp) => {
        if (resp.rfqs) {
          resp.rfqs.forEach((rfq) => {
            this.updateRowData(rfq);
          });
        }
      }),
    );
  }

  setBulkRfqSalesMembers(data) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/set_bulk_rfq_sales_members`, data).pipe(
      tap((resp) => {
        if (resp.rfqs) {
          resp.rfqs.forEach((rfq) => {
            this.updateRowData(rfq);
          });
        }
      }),
    );
  }

  setRfqAdmins(rfq_id :number, userList :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${rfq_id}/rfq_admins`, userList).pipe(
      tap((resp) => {
        if (resp.rfq) {
          this.updateRowData(resp.rfq);
        }
      }),
    );
  }

  setRfqMembers(rfq_id :number, userList :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${rfq_id}/rfq_members`, userList).pipe(
      tap((resp) => {
        if (resp.rfq) {
          this.updateRowData(resp.rfq);
        }
      }),
    );
  }

  setRfqSalesMembers(rfq_id :number, userList :any) :Observable<any> {
    return this.http
      .post(`${this.url}/rfqs/${rfq_id}/rfq_sales_members`, userList)
      .pipe(
        tap((resp) => {
          if (resp.rfq) {
            this.updateRowData(resp.rfq);
          }
        }),
      );
  }

  getDashboardRfqs(params :any) :Observable<any> {
    return this.http.get(`${this.url}/rfqs`, { params: params });
  }

  discontinue(id :number, is_discontinued :number) :Observable<any> {
    return this.http
      .post(`${this.url}/rfqs/${id}/discontinued`, {
        is_discontinued: is_discontinued,
      })
      .pipe(
        tap((data :any) => {
          this.refreshList();
          // let record = this._record.getValue();
          // if (record && record.data && record.data.length > 0) {
          // 	const idx = record.data.findIndex((row) => {
          // 		return row.id == id;
          // 	});
          // 	if (is_discontinued == 1) {
          // 		record.data.splice(idx, 1);
          // 		record.total -= 1;
          // 		this._record.next(record);
          // 	} else {
          // 		record.data[idx].is_discontinued = is_discontinued;
          // 		this._record.next(record);
          // 	}
          // }
        }),
      );
  }

  updateRfq(id :number, payload :FormData) :Observable<any> {
    payload.append('status', '1');

    return this.http.post(`${this.url}/rfqs/${id}`, payload).pipe(
      tap((data :any) => {
        const rfq = data.record;
        this.updateRowData(this.formatData(rfq));
        // if (rfq.product_variants) {
        // 	rfq.product_variants = JSON.parse(rfq.product_variants);
        // 	rfq.shipping_weight = JSON.parse(rfq.shipping_weight);
        // 	rfq.dimensional_weight = JSON.parse(rfq.dimensional_weight);
        // }
        // rfq.rfq_attachments = data.attachments;
        // try {
        // 	if (rfq.product_has_variants) {
        // 		rfq.product_variants = JSON.parse(rfq.product_variants);
        // 	}
        // } catch (e) { }
        // try {
        // 	if (rfq.product_has_variants) {
        // 		rfq.shipping_weight = JSON.parse(rfq.shipping_weight);
        // 		rfq.dimensional_weight = JSON.parse(rfq.dimensional_weight);
        // 	}
        // } catch (e) { }
        // this.updateRowData(rfq);
      }),
    );
  }

  createRfq(payload :FormData) :Observable<any> {
    return this.http.post(`${this.url}/rfqs`, payload).pipe(
      tap((data :any) => {
        const rfq = data.record;
        // rfq.unit_costs = this.getUnitCosts(0, [], 0);
        rfq.unit_costs = this.getSkuUnitCosts(rfq);
        if (rfq.product_variants) {
          rfq.product_variants = JSON.parse(rfq.product_variants);
          this.parseWeightArrayJson(rfq);
        }
        rfq.attachments = data.files;
        const record = this._record.getValue();
        if (record && record.data) {
          record.data.splice(0, 0, rfq);
          this._record.next(record);
        }
      }),
    );
  }

  uploadAttachments(id :number, fileData :FormData) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${id}/attachments`, fileData).pipe(
      tap((resp :any) => {
        const record = this._record.getValue();
        if (record && record.data && record.data.length > 0) {
          const idx = record.data.findIndex((row) => {
            return row.id == id;
          });
          const rfq = record.data[idx];
          if (rfq && rfq.rfq_attachments) {
            rfq.rfq_attachments.push(resp.record);
            record.data.splice(idx, 1, rfq);
          }
          this._record.next(record);
        }

        // let rfq = data.record;
        const activeRfq = this.getActiveRfq();
        if (resp.record.rfq_id == activeRfq.id) {
          activeRfq.rfq_attachments.push(resp.record);
          this.updateRowData(activeRfq);
        }

        // rfq.attachments = data.attachments;
        // this.updateRowData(rfq);
      }),
    );
  }

  deleteAttachment(rfqId :number, fileId :number) :Observable<any> {
    return this.http.delete(`${this.url}/rfqs/${rfqId}/attachments/${fileId}`);
  }

  getRfqQuotations(rfqId :number) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/${rfqId}/quotations`);
  }

  async toggleStarred(rfq :any) {
    const params :any = {};
    if (parseInt(rfq.starred) == 0) {
      const { value: notes } = await Swal.fire({
        title: rfq.product_name,
        input: 'textarea',
        inputPlaceholder: 'Type your notes here...',
        inputAttributes: {
          'aria-label': 'Type your notes here',
        },
        showCancelButton: true,
      });
      if (notes !== undefined) {
        params.starred_notes = notes;
      } else {
        return;
      }
    }

    return this.http
      .post(`${this.url}/rfqs/${rfq.id}/starred`, params)
      .subscribe((resp :any) => {
        const record = this._record.getValue();
        if (record && record.data && record.data.length > 0) {
          const idx = record.data.findIndex((row) => {
            return row.id == rfq.id;
          });
          if (idx || idx >= 0) {
            record.data[idx].starred = resp.record.starred;
            record.data[idx].starred_notes = resp.record.starred_notes;
          }
          this._record.next(record);
        }
      });
  }

  public firstOrderPuchase :any[] = [
    { k: 1, v: '1 - 500' },
    { k: 2, v: '501 - 1,000' },
    { k: 3, v: '1,001 - 10,000' },
    { k: 4, v: '10,000 +' },
  ];

  public annualOrderPuchase :any[] = [
    { k: 1, v: '1,000 - 5,000' },
    { k: 2, v: '5,001 - 10,000' },
    { k: 3, v: '10,001 - 50,000' },
    { k: 4, v: '50,000 +' },
  ];

  getFirstOrderPurchase(k :number) {
    const v = this.firstOrderPuchase.find((row) => {
      return row.k == k;
    });

    return v ? v.v : '';
  }

  getAnnualOrderPurchase(k :number) {
    const v = this.annualOrderPuchase.find((row) => {
      return row.k == k;
    });

    return v ? v.v : '';
  }

  getQuotationMaxPriceMarkup(quotations :any) {
    let tieredPricing :any[] = [];
    quotations.forEach((q) => {
      try {
        tieredPricing = tieredPricing.concat(JSON.parse(q.tiered_pricing_json));
      } catch (e) {}
      q.variant_quotations.forEach((vq) => {
        try {
          tieredPricing = tieredPricing.concat(JSON.parse(vq.tiered_pricing_json));
        } catch (e) {}
      });
    });

    let price :number = 0;
    let markup :number = 0;
    tieredPricing.forEach((tp) => {
      if (tp.logistic_1 > price) {
        price = tp.logistic_1;
        markup = tp.logistic_1_markup_percent;
      }
      if (tp.logistic_2 > price) {
        price = tp.logistic_2;
        markup = tp.logistic_2_markup_percent;
      }
    });

    return { price: price, markupPercentage: markup };
  }

  getSkuUnitCosts(sku :RFQ) {
    let unitCosts :any = {
      weighted: 0,
      fifo: 0,
      lastpo: 0,
      computed_weighted: 0,
      computed_fifo: 0,
      computed_lastpo: 0,
      total_cost_defective_products: 0,
      total_item_shipped: 0,
    };
    if (sku.base_price) {
      unitCosts = Object.assign({}, sku.base_price);
    }

    return unitCosts;
  }

  getUnitCosts(quantity :number, latestPurchaseOrders :any[], historyPrice :any) {
    const unitCosts :any = {
      weighted: historyPrice,
      fifo: historyPrice,
      lastPo: historyPrice,
    };
    if (latestPurchaseOrders && latestPurchaseOrders.length > 0) {
      const qt :any = latestPurchaseOrders.reduce(
        (qt, row) => {
          qt.q = qt.q + parseFloat(row.quantity);
          qt.t =
            qt.t +
            parseFloat(row.quantity) *
              (parseFloat(row.unit_price) + parseFloat(row.shipping_unit_cost));

          return qt;
        },
        { q: 0, t: 0 },
      );
      unitCosts.weighted = qt.t / qt.q;
      unitCosts.fifo = this.computeFiFoUnitCost(quantity, latestPurchaseOrders);
      unitCosts.lastPo =
        parseFloat(latestPurchaseOrders[0].unit_price) +
        parseFloat(latestPurchaseOrders[0].shipping_unit_cost);
    }

    return unitCosts;
  }

  computeFiFoUnitCost(quantity :number, latestPurchaseOrders :any) {
    let unitCost :number = 0;
    const remainingQty :number = quantity;
    for (let x = 0; x < latestPurchaseOrders.length; x++) {
      if (remainingQty <= latestPurchaseOrders[x].quantity) {
        unitCost =
          parseFloat(latestPurchaseOrders[x].unit_price) +
          parseFloat(latestPurchaseOrders[x].shipping_unit_cost);
        break;
      }
    }

    return unitCost;
  }

  importLocations(fd :FormData) {
    return this.http.post(`${this.url}/rfqs/import/locations`, fd);
  }

  transferLocations(rfqId :number, payload :any) {
    return this.http.post(`${this.url}/rfqs/${rfqId}/locations`, payload).pipe(
      tap((resp :any) => {
        if (resp.record && resp.record.id) {
          this.updateRowData(resp.record);
        }
      }),
    );
  }

  getRfqPurchaseOrders(rfqId :number) {
    return this.http.get(`${this.url}/rfqs/${rfqId}/orders`, {});
  }

  getRfqPipelines(rfqId :number) {
    return this.http.get(`${this.url}/rfqs/${rfqId}/pipelines`, {});
  }

  setRfqPipelines(rfqId :number, payload :any) {
    return this.http.post(`${this.url}/rfqs/${rfqId}/pipelines`, payload).pipe(
      tap((data :any) => {
        const record = this._record.getValue();
        if (record && record.data && record.data.length > 0) {
          const idx = record.data.findIndex((row) => {
            return row.id == rfqId;
          });
          if (idx || idx >= 0) {
            record.data[idx].pipelines = payload.pipelines;
            this._record.next(record);
          }
        }
      }),
    );
  }

  updateRfqPipelineStage(rfqId :number, pipelineId :any, payload :any) {
    return this.http.post(
      `${this.url}/rfqs/${rfqId}/pipelines/${pipelineId}`,
      payload,
    );
  }

  getActiveRetailPricesById(rfqId :number) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/${rfqId}/active_retail_prices`, {});
  }

  getActiveRetailPrices(params ?:any) :Observable<any> {
    return this.http.get(
      `${this.url}/rfqs/active_retail_prices`,
      params ? { params: params } : {},
    );
  }

  getChannelShipping(params) :Subscription {
    this._gettingRecords.next(true);

    return this.http
      .get(`${this.url}/rfqs/channel_shipping`, { params: params })
      .subscribe((resp :any) => {
        this._record_channel_shipping.next(resp);
        this._gettingRecords.next(false);
      });
  }

  updateChannelShipping(payload) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/channel_shipping`, payload);
  }

  setMaterialCost(rfqId, payload) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${rfqId}/material_cost`, payload);
  }

  recomputeMaterialCost(rfqId) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${rfqId}/recompute_material_cost`, {});
  }

  getStocks(rfqId) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/${rfqId}/stocks`);
  }

  getBasePrices(rfqId) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/${rfqId}/base_prices`);
  }

  getDept() :Observable<any> {
    return this.http.get(`${this.url}/rfqs/dept`, {});
  }

  getArchivedRfqs(params) {
    return this.http.get(`${this.url}/rfqs/archived`, { params: params });
  }

  deleteArchivedRfqs(payload) {
    return this.http.post(`${this.url}/rfqs/archived`, payload);
  }

  attachChildSku(rfqId :number, params :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/attachMaster`, params).pipe(
      tap((resp) => {
        if (resp && resp.id) {
          const activeRfq :any = this._activeRecord.getValue();
          if (activeRfq && activeRfq.id == rfqId) {
            activeRfq.alternate_master_sku.splice(0, 0, resp);
            this._activeRecord.next(activeRfq);
          }
          const record = this._record.getValue();
          if (record && record.data && record.data.length > 0) {
            const rfq :any = record.data.find((row) => {
              return row.id == rfqId;
            });
            if (rfq && rfq.id) {
              rfq.alternate_master_sku.splice(0, 0, resp);
              this._record.next(record);
            }
          }
        }
      }),
    );
  }

  detachChildSku(params) {
    return this.http.post(`${this.url}/rfqs/detachMaster`, params);
  }

  getChildSKU(rfqId) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/getAttachedSKU`, rfqId);
  }

  getAlternateSKU(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/getAlternateSKU`, payload);
  }

  saveDiscountStructure(rfq_id :number, discountStructure :any) :Observable<any> {
    return this.http
      .post(`${this.url}/rfqs/${rfq_id}/discount_structure`, discountStructure)
      .pipe(
        tap((resp) => {
          if (resp.rfq) {
            this.updateRowData(resp.rfq);
          }
        }),
      );
  }
  saveMarkupStructure(rfq_id :number, markupStructure :any) :Observable<any> {
    return this.http
      .post(`${this.url}/rfqs/${rfq_id}/markup_structure`, markupStructure)
      .pipe(
        tap((resp) => {
          if (resp.rfq) {
            this.updateRowData(resp.rfq);
          }
        }),
      );
  }

  applyDiscountStructureToVariantSku(discountStructure :any) :Observable<any> {
    return this.http.post(
      `${this.url}/rfqs/discountStructureToVariantSku`,
      discountStructure,
    );
  }

  // Variant SKUS
  createVariantSku(payload :FormData) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/variant`, payload).pipe(
      tap((data :any) => {
        const rfq = this.getActiveRfq();
        if (rfq.variant_skus) {
          rfq.variant_skus.push(data.record);
          this._activeRecord.next(rfq);
        }
      }),
    );
  }

  updateVariantSku(id :number, payload :FormData, page ?:string) :Observable<any> {
    return this.http.put(`${this.url}/rfqs/variant/${id}`, payload).pipe(
      tap((data :any) => {
        if (data.record) {
          //check if page is from quotes listing
          if (page == '/quotes') {
            const rfqs :any = this._record.getValue();
            const rfq :any = rfqs.data.find(
              (row) => row.id == data.record.variant_parent_id,
            );
            if (rfq && rfq.id) {
              const idx = rfq.variant_skus.findIndex(
                (row) => row.id == data.record.id,
              );
              if (idx !== -1) {
                rfq.variant_skus[idx] = data.record;
              }
            }
            this._record.next(rfqs);
          } else {
            const rfq = this.getActiveRfq();
            const idx = rfq.variant_skus.findIndex((row) => {
              return row.id == id;
            });
            rfq.variant_skus.splice(idx, 1, data.record);
            this._activeRecord.next(rfq);
          }
        }
      }),
    );
  }

  deleteVariantSku(id) :Observable<any> {
    return this.http.delete(`${this.url}/rfqs/variant/${id}`).pipe(
      tap((data :any) => {
        const rfq = this.getActiveRfq();
        const idx = rfq.variant_skus.findIndex((row) => {
          return row.id == id;
        });
        rfq.variant_skus.splice(idx, 1);
        this._activeRecord.next(rfq);
      }),
    );
  }

  updateProductType(id :number, params :any) :Observable<any> {
    return this.http.put(`${this.url}/rfqs/update-product-type/${id}`, params);
  }

  viewImage(id :number) {
    return this.http.get(`${this.url}/sku-image/${id}`);
  }

  uploadImages(payload :FormData, page ?:string) {
    return this.http.post(`${this.url}/sku-image`, payload).pipe(
      tap((resp :any) => {
        if (resp) {
          const activeRfq :any = this._activeRecord.getValue();
          if (activeRfq && activeRfq.id == payload.get('sku_id')) {
            resp.forEach((image) => {
              if (!activeRfq.images) {
                activeRfq.images = [];
              }
              activeRfq.images.push(image);
            });
            this._activeRecord.next(activeRfq);
          }
        }
      }),
    );
  }

  deleteImage(skuId :number, imageId :number) {
    return this.http.delete(`${this.url}/sku-image/${imageId}`).pipe(
      tap((resp :any) => {
        if (resp) {
          const activeRfq :any = this._activeRecord.getValue();
          if (activeRfq && activeRfq.id == skuId) {
            const idx = activeRfq.images.findIndex((row) => {
              return row.id == imageId;
            });
            activeRfq.images.splice(idx, 1);
            this._activeRecord.next(activeRfq);
          }
        }
      }),
    );
  }

  getDuplicateSku() {
    return this.http.get(`${this.url}/report/duplicate`, {});
  }

  bulkUpdate(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs_bulk_update`, payload).pipe(
      tap((resp :any) => {
        const record = this._record.getValue();
        if (record && record.data && record.data.length > 0) {
          resp.rfqs.forEach((rfq) => {
            this.updateRowData(this.formatData(rfq), resp);
          });
        }
      }),
    );
  }

  public resetBaselineToZero(params :any) :Observable<any> {
    return this.http.put(`${this.url}/rfqs/resetBaselineToZero`, {
      params: params,
    });
  }

  public removeSkuProductVariantStockEntry(id :number) :Observable<any> {
    return this.http.delete(
      `${this.url}/rfqs/skuProductVariantStockEntry/${id}`,
      {},
    );
  }

  private parseWeightArrayJson(rfq :RFQ) :void {
    rfq.shipping_weight = JSON.parse(rfq.shipping_weight);
    rfq.product_weight = JSON.parse(rfq.product_weight);
    rfq.box_weight = JSON.parse(rfq.box_weight);
    rfq.carton_weight = JSON.parse(rfq.carton_weight);
    rfq.pallet_weight = JSON.parse(rfq.pallet_weight);
    rfq.dimensional_weight = JSON.parse(rfq.dimensional_weight);
  }

  searchParentSKUDropdown(params :any) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/search_parent_sku_dropdown`, {
      params: params,
    });
  }

  moveSkuToMasterSku(params :any, id :number) :Observable<any> {
    return this.http.post(
      `${this.urlV2}/inventory/consolidate-master-and-variant-skus/${id}`,
      params,
    );
  }

  setRfqs(rfqs :RFQ[]) :void {
    this._rfqs.next(rfqs);
  }

  generateSku() :Subscription {
    this._generatingSku.next(true);
    this._generatedSku.next(null);

    return this.http.get(`${this.url}/rfqs/generate-sku`).subscribe(
      (response :any) => {
        this._generatingSku.next(false);
        this._generatedSku.next(response.generated_sku || '');
      },
      (response :any) => throwError(response.error),
    );
  }

  getRfqPrice(params :{
    rfqId :number;
    quantity :number;
    uomId :number;
    businessAccountId :number;
  }) :Observable<any> {
    return this.http.get(`${this.urlV2}/pricing/price`, { params: params });
  }

  toggleRfqStarredState(params :any, rfqId :number = 0) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/${rfqId}/starred`, params);
  }

  getConsolidatedSku(rfqId :number = 0) :Subscription {
    this._gettingSkus.next(true);

    return this.http.get(`${this.url}/rfqs/${rfqId}`).subscribe(
      (response :any) => {
        this._gettingSkus.next(false);
        this._consolidatedSku.next(response);
      },
      (response :any) => {
        this._gettingSkus.next(false);
        throwError(response.error);
      },
    );
  }

  getLocationStocksByRfqIds(params :{
    rfqIds :string;
    warehouseGroupId :number;
  }) :Observable<any> {
    return this.http.get(`${this.url}/rfqs/location-stocks-by-rfq-ids`, {
      params: params,
    });
  }

  promoteVariantToMasterSku(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/rfqs/variant-master-skus`, payload);
  }

  detachBOMRfq(payload :{ rfqId :number; bomRfqId :number }) :Observable<any> {
    return this.http.post(`${this.urlV2}/rfqs/detach-bom`, payload);
  }
}
